import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';
import type {
  CACAcceptOfferResponse,
  CACApprovePublicationInput,
  CACCatalogItem,
  CACImportResponse,
  CACPublicationRequest,
  CACRegisterArtifactInput,
  CACRegisterArtifactResponse,
  CACRequestPublicationInput,
  ContractAcceptance,
  ContractAcceptanceCreatePayload,
  KycCheck,
  KycEvidenceSubmitPayload,
  KycReviewPayload,
  LicenseTemplate,
  LicenseTemplateCreatePayload,
  LicenseTemplateDetail,
  LicenseTemplateUpdatePayload,
  PartnerProfile,
  PartnerTierTransition,
  PlatformEntity,
  PricingCalculationResponse,
  PricingModel,
  PricingModelCreatePayload,
  PricingModelDetail,
  RevenueTransaction,
  Settlement,
} from '../types';

const CAC_KEY = 'cac';

function newIdempotencyKey(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useCACCatalog(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'catalog', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<CACCatalogItem[]>('/cac/catalog', {
        params: tenantId ? { tenant_id: tenantId } : undefined,
      });
      return (response.data ?? []) as CACCatalogItem[];
    },
    enabled: !!tenantId,
  });
}

export function useCACMyArtifacts(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'my-artifacts', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<{ data: Array<{ artifact_id: string; artifact_slug: string; kind: string; version_id: string | null; version: string | null; lifecycle_status: string }> }>('/cac/my-artifacts', {
        params: tenantId ? { tenant_id: tenantId } : undefined,
      });
      return response.data.data ?? response.data ?? [];
    },
    enabled: !!tenantId,
  });
}

export function useCACAcceptOffer(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { offerId: string; acceptedById: string }) => {
      if (!tenantId) {
        throw new Error('Tenant is required');
      }
      const response = await rawApi.post<{ data: CACAcceptOfferResponse }>(
        `/cac/offers/${encodeURIComponent(input.offerId)}/accept`,
        {
          tenant_id: tenantId,
          accepted_by_id: input.acceptedById,
        },
        {
          headers: {
            'Idempotency-Key': newIdempotencyKey('cac-accept-offer'),
          },
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'catalog'] });
    },
  });
}

export function useCACPublicationRequests(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'publication-requests', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<{ data: CACPublicationRequest[] }>('/cac/publication-requests', {
        params: tenantId ? { tenant_id: tenantId } : undefined,
      });
      return response.data.data ?? [];
    },
    enabled: !!tenantId,
  });
}

export function useCACImportPackage(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      versionId: string;
      regionCode: string;
      acceptedById: string;
    }) => {
      if (!tenantId) {
        throw new Error('Tenant is required');
      }
      const response = await rawApi.post<{ data: CACImportResponse }>(
        `/cac/packages/${encodeURIComponent(input.versionId)}/imports`,
        {
          tenant_id: tenantId,
          region_code: input.regionCode,
          accepted_by_id: input.acceptedById,
        },
        {
          headers: {
            'Idempotency-Key': newIdempotencyKey('cac-import-package'),
          },
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'publication-requests'] });
    },
  });
}

export function useCACRequestPublication(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<CACRequestPublicationInput, 'requester_tenant_id' | 'requested_by_id'> & { requestedById: string }) => {
      if (!tenantId) throw new Error('Tenant is required');
      const response = await rawApi.post<{ data: CACPublicationRequest }>(
        '/cac/publication-requests',
        {
          artifact_id: input.artifact_id,
          version_id: input.version_id,
          requester_tenant_id: tenantId,
          requested_by_id: input.requestedById,
          requested_level: input.requested_level,
          delivery_mode: input.delivery_mode,
          license_template_ref: input.license_template_ref,
        } satisfies CACRequestPublicationInput,
        { headers: { 'Idempotency-Key': newIdempotencyKey('cac-pub-request') } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'publication-requests'] });
    },
  });
}

export function useCACAllPublicationRequests() {
  return useQuery({
    queryKey: [CAC_KEY, 'approvals'],
    queryFn: async () => {
      const response = await rawApi.get<{ data: CACPublicationRequest[] }>('/cac/publication-requests/pending');
      return response.data.data ?? [];
    },
  });
}

// ── Platform Entity Registration (Sprint 188) ─────────────────────

export function usePlatformEntities(tenantId: string | null | undefined, kind?: string) {
  return useQuery({
    queryKey: [CAC_KEY, 'platform-entities', tenantId, kind],
    queryFn: async () => {
      const response = await rawApi.get<PlatformEntity[]>('/cac/platform-entities', {
        params: { tenant_id: tenantId, ...(kind ? { kind } : {}) },
      });
      return response.data ?? [];
    },
    enabled: !!tenantId,
  });
}

export function useCACRegisterArtifact(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CACRegisterArtifactInput) => {
      if (!tenantId) throw new Error('Tenant is required');
      const response = await rawApi.post<CACRegisterArtifactResponse>(
        '/cac/artifacts/register',
        {
          entity_type: input.entity_type,
          entity_id: input.entity_id,
          name: input.name,
          slug: input.slug,
          summary: input.summary,
          description: input.description,
          visibility: input.visibility ?? 'private',
          labels: input.labels ?? [],
          include_dependencies: input.include_dependencies ?? true,
          version: input.version || undefined,
        },
        { params: { tenant_id: tenantId } },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'catalog'] });
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'platform-entities'] });
    },
  });
}

export function useCACApprovePublication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { requestId: string } & CACApprovePublicationInput) => {
      const response = await rawApi.post<{ data: CACPublicationRequest }>(
        `/cac/publication-requests/${encodeURIComponent(input.requestId)}/approve`,
        {
          approved_level: input.approved_level,
          harbor_digest: input.harbor_digest,
        } satisfies CACApprovePublicationInput,
        { headers: { 'Idempotency-Key': newIdempotencyKey('cac-approve') } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'approvals'] });
    },
  });
}

// ── License Templates ───────────────────────────────────────────────

export function useCACLicenseTemplates(status?: string) {
  return useQuery({
    queryKey: [CAC_KEY, 'license-templates', status],
    queryFn: async () => {
      const response = await rawApi.get<LicenseTemplate[]>('/cac/license-templates', {
        params: status ? { status } : undefined,
      });
      return response.data ?? [];
    },
  });
}

export function useCACLicenseTemplate(slug: string) {
  return useQuery({
    queryKey: [CAC_KEY, 'license-template', slug],
    queryFn: async () => {
      const response = await rawApi.get<LicenseTemplateDetail>(
        `/cac/license-templates/${encodeURIComponent(slug)}`
      );
      return response.data;
    },
    enabled: !!slug,
  });
}

export function useCACCreateLicenseTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LicenseTemplateCreatePayload) => {
      const response = await rawApi.post<LicenseTemplate>(
        '/cac/license-templates',
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'license-templates'] });
    },
  });
}

export function useCACUpdateLicenseTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { slug: string; data: LicenseTemplateUpdatePayload }) => {
      const response = await rawApi.put<LicenseTemplate>(
        `/cac/license-templates/${encodeURIComponent(input.slug)}`,
        input.data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'license-templates'] });
    },
  });
}

export function useCACDeprecateLicenseTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await rawApi.post<LicenseTemplate>(
        `/cac/license-templates/${encodeURIComponent(slug)}/deprecate`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'license-templates'] });
    },
  });
}

// ── Contract Acceptance ─────────────────────────────────────────────

export function useCACContractAcceptance(_tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ContractAcceptanceCreatePayload) => {
      if (!input.tenant_id) {
        throw new Error('Tenant is required');
      }
      const response = await rawApi.post<ContractAcceptance>(
        '/cac/contract-acceptances',
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'contract-acceptances'] });
    },
  });
}

export function useCACActiveAcceptance(
  tenantId: string | null | undefined,
  offerId: string | null | undefined,
) {
  return useQuery({
    queryKey: [CAC_KEY, 'contract-acceptances', tenantId, offerId],
    queryFn: async () => {
      if (!tenantId || !offerId) return null;
      const response = await rawApi.get<ContractAcceptance[]>(
        '/cac/contract-acceptances',
        { params: { tenant_id: tenantId, offer_id: offerId } },
      );
      const list = response.data ?? [];
      return list.length > 0 ? list[0] : null;
    },
    enabled: !!tenantId && !!offerId,
  });
}

// ── Partner Profile & Tier ────────────────────────────────────────────

export function useCACPartnerProfile(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'partner-profile', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<PartnerProfile>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId as string)}`,
      );
      return response.data;
    },
    enabled: !!tenantId,
    retry: false,
  });
}

export function useCACUpdatePartnerProfile(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Pick<PartnerProfile, 'display_name' | 'slug' | 'contact_email'>>) => {
      if (!tenantId) throw new Error('Tenant is required');
      const response = await rawApi.put<PartnerProfile>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId)}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'partner-profile'] });
    },
  });
}

export function useCACRequestTierUpgrade(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestedById: string) => {
      if (!tenantId) throw new Error('Tenant is required');
      const response = await rawApi.post<PartnerTierTransition>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId)}/request-tier-upgrade`,
        { requested_by_id: requestedById },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'partner-profile'] });
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'tier-transitions'] });
    },
  });
}

export function useCACTierTransitions(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'tier-transitions', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<PartnerTierTransition[]>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId as string)}/tier-transitions`,
      );
      return response.data ?? [];
    },
    enabled: !!tenantId,
  });
}

// ── KYC Checks ─────────────────────────────────────────────────────────

export function useCACKycChecks(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'kyc-checks', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<KycCheck[]>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId as string)}/kyc-checks`,
      );
      return response.data ?? [];
    },
    enabled: !!tenantId,
  });
}

export function useCACSubmitKycEvidence(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { checkSlug: string; payload: KycEvidenceSubmitPayload }) => {
      if (!tenantId) throw new Error('Tenant is required');
      const response = await rawApi.post<KycCheck>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId)}/kyc-checks/${encodeURIComponent(input.checkSlug)}/submit`,
        input.payload,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'kyc-checks'] });
    },
  });
}

export function useCACReviewKycCheck(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { checkSlug: string; payload: KycReviewPayload }) => {
      const response = await rawApi.post<KycCheck>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId as string)}/kyc-checks/${encodeURIComponent(input.checkSlug)}/review`,
        input.payload,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'kyc-checks'] });
    },
  });
}

export function useCACResetKycCheck(tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkSlug: string) => {
      const response = await rawApi.post<KycCheck>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId as string)}/kyc-checks/${encodeURIComponent(checkSlug)}/reset`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'kyc-checks'] });
    },
  });
}

// ── Admin ──────────────────────────────────────────────────────────────

export function useCACPartnerProfilesList(tier?: string, verificationStatus?: string) {
  return useQuery({
    queryKey: [CAC_KEY, 'partner-profiles', tier, verificationStatus],
    queryFn: async () => {
      const response = await rawApi.get<PartnerProfile[]>('/cac/partner-profiles', {
        params: {
          ...(tier ? { tier } : {}),
          ...(verificationStatus ? { verification_status: verificationStatus } : {}),
        },
      });
      return response.data ?? [];
    },
  });
}

export function useCACTransitionTier(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      transition_type: string;
      to_tier?: string;
      reason?: string;
    }) => {
      const response = await rawApi.post<PartnerProfile>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId)}/transition-tier`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'partner-profiles'] });
    },
  });
}

export function useCACRejectTierTransition(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await rawApi.post<PartnerTierTransition>(
        `/cac/partner-profiles/${encodeURIComponent(tenantId)}/reject-tier-transition`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'partner-profiles'] });
    },
  });
}

// ── Revenue & Settlements ─────────────────────────────────────────────

export function useCACRevenueTransactions(
  tenantId: string | null | undefined,
  filters?: { period_start?: string; period_end?: string },
) {
  return useQuery({
    queryKey: [CAC_KEY, 'revenue-transactions', tenantId, filters],
    queryFn: async () => {
      const response = await rawApi.get<RevenueTransaction[]>('/cac/revenue/transactions', {
        params: {
          publisher_tenant_id: tenantId,
          ...(filters?.period_start ? { period_start: filters.period_start } : {}),
          ...(filters?.period_end ? { period_end: filters.period_end } : {}),
        },
      });
      return response.data ?? [];
    },
    enabled: !!tenantId,
  });
}

export function useCACAllRevenueTransactions() {
  return useQuery({
    queryKey: [CAC_KEY, 'revenue-transactions', 'all'],
    queryFn: async () => {
      const response = await rawApi.get<RevenueTransaction[]>('/cac/revenue/transactions');
      return response.data ?? [];
    },
  });
}

export function useCACSettlements(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'settlements', tenantId],
    queryFn: async () => {
      const response = await rawApi.get<Settlement[]>('/cac/settlements', {
        params: tenantId ? { partner_tenant_id: tenantId } : undefined,
      });
      return response.data ?? [];
    },
  });
}

export function useCACApproveSettlement(_tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settlementId: string) => {
      const response = await rawApi.post<Settlement>(
        `/cac/settlements/${encodeURIComponent(settlementId)}/approve`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'settlements'] });
    },
  });
}

export function useCACDisputeSettlement(_tenantId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { settlementId: string; reason: string }) => {
      const response = await rawApi.post<Settlement>(
        `/cac/settlements/${encodeURIComponent(input.settlementId)}/dispute`,
        { reason: input.reason },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'settlements'] });
    },
  });
}

export function useCACRecalculateSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { settlementId: string; adminNotes?: string }) => {
      const response = await rawApi.post<Settlement>(
        `/cac/settlements/${encodeURIComponent(input.settlementId)}/recalculate`,
        { admin_notes: input.adminNotes },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'settlements'] });
    },
  });
}

export function useCACMarkSettlementPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { settlementId: string; arPayoutRef: string }) => {
      const response = await rawApi.post<Settlement>(
        `/cac/settlements/${encodeURIComponent(input.settlementId)}/mark-paid`,
        { ar_payout_ref: input.arPayoutRef },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'settlements'] });
    },
  });
}

// ── Pricing Models ────────────────────────────────────────────────────

export function useCACOfferPricing(offerId: string | null | undefined) {
  return useQuery({
    queryKey: [CAC_KEY, 'pricing', offerId],
    queryFn: async () => {
      const response = await rawApi.get<PricingModelDetail>(
        `/cac/offers/${encodeURIComponent(offerId as string)}/pricing`,
      );
      return response.data;
    },
    enabled: !!offerId,
  });
}

export function useCACCreatePricingModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PricingModelCreatePayload) => {
      const response = await rawApi.post<PricingModel>('/cac/pricing-models', input);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'pricing'] });
    },
  });
}

export function useCACCalculatePricing(modelId: string | null | undefined, units: number) {
  return useQuery({
    queryKey: [CAC_KEY, 'pricing-calc', modelId, units],
    queryFn: async () => {
      const response = await rawApi.get<PricingCalculationResponse>(
        `/cac/pricing-models/${encodeURIComponent(modelId as string)}/calculate`,
        { params: { units } },
      );
      return response.data;
    },
    enabled: !!modelId,
  });
}

// ── REQ-CAC-021 — Artifact owner lifecycle ─────────────────────────

export function useCACDecommissionArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (artifactId: string) => {
      const response = await rawApi.patch<unknown>(
        `/cac/artifacts/${artifactId}/decommission`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'catalog'] });
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'my-artifacts'] });
    },
  });
}

export function useCACDeleteArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (artifactId: string) => {
      const response = await rawApi.delete<unknown>(`/cac/artifacts/${artifactId}`);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'catalog'] });
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'my-artifacts'] });
    },
  });
}

export function useCACUpdateArtifactLicensing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      artifactId: string;
      license_profile_id?: string;
      visibility?: 'private' | 'tenant' | 'public' | 'marketplace';
    }) => {
      const response = await rawApi.patch<unknown>(
        `/cac/artifacts/${input.artifactId}/licensing`,
        {
          license_profile_id: input.license_profile_id,
          visibility: input.visibility,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'catalog'] });
      qc.invalidateQueries({ queryKey: [CAC_KEY, 'my-artifacts'] });
    },
  });
}
